import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "../../../components/layouts/Cards";
import { Badge } from "../../../components/text/Badge";
import {
  toggleFilter,
  selectActiveFilters,
  setDateFilter,
  clearDateFilter,
} from "../../../store/civicPulseSlice";
import { DEMOGRAPHIC_FILTERS, TAG_FILTERS } from "../config/index";
import { formatTitle } from "../../../utility/formatText";
import { fetchUniverseSize } from "../services/api";
import { quantum } from "ldrs";
import DateFilterSection from "./DateFilterSection";
import useCancelableRequest from "../hooks/useCancelableRequest";
quantum.register();

const FilterPanel = () => {
  const dispatch = useDispatch();
  // Returns a stable reference when content hasn't changed, preventing unnecessary re-renders
  const activeFilters = useSelector(selectActiveFilters);
  const { demographics = {}, tags, dateFilter = {} } = activeFilters;
  const [searchQuery, setSearchQuery] = useState("");

  // Universe size - now uses stable activeFilters reference
  const { data: universeData, loading: loadingUniverse } = useCancelableRequest(
    fetchUniverseSize,
    [activeFilters],
    [activeFilters]
  );

  // Handle new API response format
  const universeSize =
    universeData && Array.isArray(universeData) && universeData[0]?.total_unweighted_count !== undefined
      ? universeData[0].total_unweighted_count
      : universeData?.weighted_count || null; // Fallback to old format

  // Handler for grouped demographics
  const handleDemographicToggle = (category, value) => {
    dispatch({
      type: "civicPulse/toggleDemographicFilter",
      payload: { category, value },
    });
  };

  const handleFilterToggle = (type, value) => {
    dispatch(toggleFilter({ type, value }));
  };

  // For tags: filter by search
  const filteredTags = TAG_FILTERS.filter(
    (tag) =>
      !searchQuery ||
      tag.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Selected first, then unselected
  const sortedTags = [
    ...filteredTags.filter((f) => tags.includes(f.label)),
    ...filteredTags.filter((f) => !tags.includes(f.label)),
  ];

  return (
    <div className="p-6 pt-32">
      {/* Universe size display */}
      <div className="mb-4 p-2">
        {universeSize === null ? (
          <span className="flex justify-center items-center w-full">
            <l-quantum size="32" speed="1.2" color="black"></l-quantum>
          </span>
        ) : (
          <span className="font-bold text-lg">
            <span className="text-black">Universe Size </span>
            <span className="text-mblue-700">
              {universeSize?.toLocaleString()}
            </span>
          </span>
        )}
      </div>
      
      {/* Date Filter Section */}
      <Card className="!shadow-none mb-4">
        <DateFilterSection
          value={dateFilter}
          onChange={(val) => dispatch(setDateFilter(val))}
          onClear={() => dispatch(clearDateFilter())}
        />
      </Card>
      
      <Card className="!shadow-none">
        <h2 className="font-bold text-lg mb-4">Filter by Demographics</h2>
        <div className="mb-6">
          {Object.entries(DEMOGRAPHIC_FILTERS).map(([category, options]) => (
            <div key={category} className="mb-4">
              <h3 className="mb-2">{formatTitle(category)}</h3>
              <div className="flex flex-wrap gap-2">
                {options.map((option) => {
                  // Handle both old string format and new object format
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  
                  return (
                    <Badge
                      key={optionValue}
                      color="morange"
                      isButton={true}
                      active={demographics[category]?.includes(optionValue)}
                      onClick={() => handleDemographicToggle(category, optionValue)}
                    >
                      {formatTitle(optionLabel)}
                    </Badge>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        <h2 className="font-bold text-lg mt-1 mb-2">Filter by Tags</h2>
        <div className="relative mb-2">
          <input
            type="text"
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-msky-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          )}
        </div>
        
        <div
          className="flex flex-wrap gap-2 overflow-y-auto pb-2 max-h-48"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {sortedTags.map((filter) => (
            <Badge
              key={filter.label}
              color="msky"
              isButton={true}
              active={tags.includes(filter.label)}
              onClick={() => handleFilterToggle("tags", filter.label)}
            >
              {formatTitle(filter.label)}
            </Badge>
          ))}
          {sortedTags.length === 0 && (
            <p className="text-slate-500 text-sm italic">
              No matching tags found
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FilterPanel;